/**
 * Ambience cross-fading. A level change swaps one bed for another over
 * `TUNING.audio.ambienceFade` seconds — long enough that the player is already
 * somewhere else before they notice the old room stopped.
 *
 * Both beds run during the fade; the outgoing one is torn down afterwards so a
 * long session with many descents does not accumulate oscillators.
 */

import { TUNING } from '../core/config';
import { clamp01 } from '../core/math';
import type { AmbienceProfile } from '../core/types';
import { makeBed, type Bed } from './beds';
import type { AudioGraph } from './graph';
import type { Sfx } from './sfx';

interface Slot {
  profile: AmbienceProfile;
  bed: Bed;
  send: GainNode;
  /** Context time at which the bed may be torn down. Infinity while current. */
  disposeAt: number;
}

export class Ambience {
  private current: Slot | null = null;
  private readonly retiring: Slot[] = [];
  private intensity = 1;

  constructor(
    private readonly g: AudioGraph,
    private readonly sfx: Sfx,
  ) {}

  get profile(): AmbienceProfile | null {
    return this.current?.profile ?? null;
  }

  set(profile: AmbienceProfile, intensity = 1): void {
    this.intensity = clamp01(intensity);
    const fade = TUNING.audio.ambienceFade;

    if (this.current && this.current.profile === profile) {
      this.ramp(this.current, this.intensity, 0.6);
      return;
    }

    this.g.useReverb(profile);

    if (this.current) {
      this.ramp(this.current, 0, fade);
      this.current.disposeAt = this.g.now + fade + 0.15;
      this.retiring.push(this.current);
    }

    const bed = makeBed(profile, this.g, this.sfx);
    const send = this.g.ctx.createGain();
    send.gain.value = bed.send;
    bed.out.connect(this.g.bed);
    bed.out.connect(send);
    send.connect(this.g.reverbIn);

    const slot: Slot = { profile, bed, send, disposeAt: Infinity };
    // A first bed comes up quickly; a swap matches the outgoing fade.
    this.ramp(slot, this.intensity, this.current ? fade : 0.8);
    this.current = slot;
  }

  update(dt: number, unease: number): void {
    this.current?.bed.update(dt, unease);
    for (let i = this.retiring.length - 1; i >= 0; i--) {
      const slot = this.retiring[i]!;
      if (this.g.now >= slot.disposeAt) {
        slot.bed.dispose();
        slot.send.disconnect();
        this.retiring.splice(i, 1);
      } else {
        // Keep updating it: a bed that stops modulating mid-fade is audible.
        slot.bed.update(dt, unease);
      }
    }
  }

  private ramp(slot: Slot, target: number, seconds: number): void {
    const t = this.g.now;
    const p = slot.bed.out.gain;
    p.cancelScheduledValues(t);
    p.setValueAtTime(p.value, t);
    p.linearRampToValueAtTime(target, t + Math.max(0.05, seconds));
  }
}
